import React from "react";
import { merge, $ } from "glamor";
import { ColumnApi, ColumnStateParams, Events, IToolPanelParams } from "ag-grid-community";
import { ColumnTypes } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import { ToolPanels } from "constants/product-collection";
import { formatMessage } from "lib/globalize-util";
import { makeSortState, mapSortMapToColumnState } from "./helpers";

export type SortState = Record<string, ColumnStateParams["sort"]>;

interface IProps extends IToolPanelParams {
    onSortChange: (sort: SortState) => void;
    hideColumnIds: string[];
    columnApi: ColumnApi;
}

const ARROWS = {
    asc: "↑",
    desc: "↓",
};

const AG_GRID_AUTOCOLUMN = "ag-Grid-AutoColumn";

const styles = merge(
    {
        width: "199px",
    },
    $(" .sortableField", {
        padding: "5px",
        display: "flex",
    }),
    $(" .sortableFieldLabel", {
        color: "rgba(0, 0, 0, 0.54)",
        fontWeight: "600",
        lineHeight: "17.14px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "173px",
        cursor: "pointer",
    }),
    $(" .sortIcon", {
        height: "16px",
        width: "16px",
        color: "#7F8C8D",
        lineHeight: "16px",
        textAlign: "center",
    })
);
export const SortToolPanel: React.FunctionComponent<IProps> = ({ api, columnApi, hideColumnIds = [] }) => {
    const [sortMap, setSortMap] = React.useState<SortState>({});
    const [multiSort, setMultisort] = React.useState<boolean>(false);
    const [activePanel, setActivePanel] = React.useState<string>(ToolPanels.Columns);

    const handleKeyDown = (e) => e.shiftKey && setMultisort(true);

    const handleKeyUp = () => setMultisort(false);

    const updateSortMap = React.useCallback(() => {
        const updatedSortMap = columnApi.getColumnState().reduce((acc, { colId, sort }) => {
            if (sort) {
                acc[colId] = sort;
            }
            return acc;
        }, {});
        setSortMap(updatedSortMap);
    }, [columnApi]);

    const updateActivePanel = React.useCallback(() => setActivePanel(api.getOpenedToolPanel()), [api]);

    React.useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        api.addEventListener(Events.EVENT_SORT_CHANGED, updateSortMap);
        api.addEventListener(Events.EVENT_TOOL_PANEL_VISIBLE_CHANGED, updateActivePanel);
        updateSortMap();
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
            api.removeEventListener(Events.EVENT_SORT_CHANGED, updateSortMap);
            api.removeEventListener(Events.EVENT_TOOL_PANEL_VISIBLE_CHANGED, updateActivePanel);
        };
    }, [api, updateActivePanel, updateSortMap]);

    const reSort = React.useCallback(
        (colId, multiSort, sortMap) => {
            const nextSortMap = makeSortState(colId, sortMap, multiSort);
            const sortArray = mapSortMapToColumnState(nextSortMap);
            setSortMap(nextSortMap);
            columnApi.applyColumnState({ state: sortArray });
        },
        [columnApi]
    );

    if (activePanel !== ToolPanels.Sort) {
        return <div className="sortableField">{formatMessage("Loading...")}</div>;
    }
    // @ts-ignore columnModel is private - TODO: use columnApi.getAllGridColumns() instead and fix (c.type is not resolved)
    const allGridColumns = api.columnModel.getAllGridColumns();
    const columns = allGridColumns.filter(
        (c) => c.colDef.type !== ColumnTypes.AllocationQuantity && !hideColumnIds.includes(c.colId)
    );
    const groupColName = allGridColumns
        .filter((col) => col.rowGroupActive)
        .map((col) => col?.colDef?.headerName)
        .join("→");

    return (
        <div {...styles}>
            {columns.map((col) => {
                const colId = col?.colId;
                const icon = ARROWS?.[sortMap?.[colId]];
                let colName = col.colDef?.headerName;
                if (col.colId === AG_GRID_AUTOCOLUMN) {
                    colName += `: ${groupColName}`;
                }
                return (
                    <SortPanelRow
                        key={colId}
                        colId={colId}
                        icon={icon}
                        colName={colName}
                        onClick={() => reSort(colId, multiSort, sortMap)}
                    />
                );
            })}
        </div>
    );
};

const SortPanelRow = ({ colId, colName, icon, onClick }) => {
    return (
        <div className="sortableField" data-key={colId}>
            <div className="sortIcon">{icon}</div>
            <div className="sortableFieldLabel" onClick={onClick}>
                {colName}
            </div>
        </div>
    );
};
